import { API } from "@swivl-mono/api-helper";
import { RoutingGroupInterface } from "@swivl-mono/types";
import { Select, SelectItem } from "@swivl-mono/ui";
import React, { useEffect, useMemo, useState } from "react";
import { IoIosArrowDown } from "react-icons/io";

import { Member } from "../../../../Models/Member/Member.Current";
import { TicketPropertyRow } from "./TicketPropertyRow";

interface GroupFieldProps {
  routingGroupSlug: string | null | undefined;
  /** Server-resolved name, used when locked mode skips the live group list. */
  resolvedGroupName?: string | null;
  onChange: (routingGroupSlug: string) => void;
  pending?: boolean;
  locked?: boolean;
}

/**
 * Detail-panel "Group" field. Lists the org's routing groups (deduped by
 * slug) and re-dispatches the ticket to the chosen group on change.
 */
export function GroupField({ routingGroupSlug, resolvedGroupName, onChange, pending, locked }: GroupFieldProps): React.JSX.Element {
  const [groups, setGroups] = useState<RoutingGroupInterface[]>([]);

  useEffect(() => {
    if (locked) return;
    let active = true;
    void API.RoutingGroup.listGroups(Member.orgId)
      .then((rows) => {
        if (active) setGroups(rows);
      })
      .catch(() => {
        if (active) setGroups([]);
      });
    return () => {
      active = false;
    };
  }, [locked]);

  // Distinct, non-archived group slugs (a group may have rows at multiple
  // scopes — show each named team once).
  const nameBySlug = useMemo(() => {
    const bySlug = new Map<string, string>();
    for (const g of groups) {
      if (g.isArchived) continue;
      if (!bySlug.has(g.slug)) bySlug.set(g.slug, g.name);
    }
    return bySlug;
  }, [groups]);

  const options: SelectItem[] = useMemo(() => Array.from(nameBySlug.entries()).map(([slug, name]) => ({ label: name, value: slug })), [nameBySlug]);

  const displayText = !routingGroupSlug ? "No group" : (nameBySlug.get(routingGroupSlug) ?? resolvedGroupName ?? routingGroupSlug);

  const handleChange = (selected: string[]): void => {
    const val = selected[0];
    if (val && val !== routingGroupSlug) onChange(val);
  };

  if (locked) {
    return (
      <TicketPropertyRow label="Group" lockedAction="Updating groups">
        {displayText}
      </TicketPropertyRow>
    );
  }

  return (
    <TicketPropertyRow label="Group" pending={pending}>
      <Select
        options={options}
        selectedValues={routingGroupSlug ? [routingGroupSlug] : []}
        onChange={handleChange}
        hideClear
        // Menu width otherwise locks to the narrow trigger, clipping longer
        // group names; size it to content (min trigger width, capped).
        styles={{ popover: { width: "max-content", minWidth: "10rem", maxWidth: "16rem" } }}
        customTrigger={({ isOpen }) => (
          <button className={`flex cursor-pointer items-center gap-1 rounded px-2 py-0.5 text-sm ${isOpen ? "bg-grey-100" : "hover:bg-grey-50"}`}>
            {displayText}
            <IoIosArrowDown className={`h-3.5 w-3.5 text-grey-600 transition-transform ${isOpen ? "rotate-180" : ""}`} />
          </button>
        )}
      />
    </TicketPropertyRow>
  );
}
